import type { AiCompanionAttendanceProposal } from "@simbelapp/order-sdk";
import { isToolUIPart } from "ai";
import { AttendanceRunStatusEnum } from "~/utils/enums/attendance-review.enums";
import { CopilotDataPartEnum, CopilotToolStateEnum } from "~/utils/enums/copilot/chat.enums";
import { CopilotErrorCodeEnum } from "~/utils/enums/copilot/errors.enums";
import { normalizeCopilotError } from "~/utils/helpers/copilot/errors";
import type {
  IAttendanceProposalEnvelope,
  IAttendanceRunEnvelope,
} from "~/utils/interfaces/attendance-review-interfaces";
import type { ICopilotStreamRead, ICopilotUIMessage } from "~/utils/interfaces/copilot-interfaces";

/** The three buckets together are what makes a payload an attendance proposal and nothing else. */
function isProposal(value: unknown): value is AiCompanionAttendanceProposal {
  const candidate = value as AiCompanionAttendanceProposal | null;

  return (
    !!candidate &&
    typeof candidate === "object" &&
    Array.isArray(candidate.matched) &&
    Array.isArray(candidate.notFoundInSession) &&
    Array.isArray(candidate.notFoundInFile)
  );
}

/**
 * Is this streamed payload an attendance proposal? Recognised structurally rather than by the name
 * of the tool carrying it: the name belongs to the backend, and a stale guess fails silently — the
 * review simply never opens. Accepts both the wrapped response and the bare proposal, since only
 * confirming needs the run ids — reviewing does not.
 */
export function asAttendanceRunEnvelope(payload: unknown): IAttendanceRunEnvelope | null {
  const candidate = payload as (Partial<IAttendanceRunEnvelope> & { proposal?: unknown }) | null;
  if (!candidate || typeof candidate !== "object") return null;

  const document = candidate.document;
  const shared = {
    runId: typeof candidate.runId === "string" ? candidate.runId : null,
    sessionId: typeof candidate.sessionId === "string" ? candidate.sessionId : null,
    timeslotId: typeof candidate.timeslotId === "string" ? candidate.timeslotId : null,
    document: document?.url ? { fileName: document.fileName ?? "", url: document.url } : null,
  };

  if (candidate.status === AttendanceRunStatusEnum.SESSION_CHOICE) {
    if (!shared.runId || !Array.isArray(candidate.candidates)) return null;

    const candidates = candidate.candidates.filter(
      (session) =>
        !!session &&
        typeof session === "object" &&
        typeof session.sessionId === "string" &&
        typeof session.matchedBy === "string",
    );

    return {
      ...shared,
      status: AttendanceRunStatusEnum.SESSION_CHOICE,
      proposal: null,
      candidates,
      // Why the choice is being asked, verbatim — it is already user-facing French.
      warning: typeof candidate.warning === "string" && candidate.warning.trim() ? candidate.warning : null,
    };
  }

  const proposal = isProposal(candidate.proposal) ? candidate.proposal : isProposal(candidate) ? candidate : null;
  if (!proposal) return null;

  // Read off either the envelope or the proposal: which level carries the ids is the backend's call.
  const nested = proposal as Partial<IAttendanceRunEnvelope>;
  const pick = (key: "runId" | "sessionId" | "timeslotId") => {
    const value = candidate[key] ?? nested[key];

    return typeof value === "string" ? value : null;
  };
  const proposalDocument = candidate.document ?? nested.document;

  return {
    runId: pick("runId"),
    status: AttendanceRunStatusEnum.PROPOSAL,
    sessionId: pick("sessionId"),
    timeslotId: pick("timeslotId"),
    document: proposalDocument?.url ? { fileName: proposalDocument.fileName ?? "", url: proposalDocument.url } : null,
    proposal,
    candidates: [],
    warning: null,
  };
}

/**
 * Every attendance run a streamed conversation carries, in arrival order, each paired with the
 * identity of the *part* that carries it — never with its `runId`. Correcting the session in the
 * chat ("la bonne session est …") calls the tool again on the same paused run and returns a new
 * candidate list, so a run appears more than once and the last one is the one to render.
 */
/** The run a stream part carries, or `null` — the one place a part is read as a run. */
function runOfPart(part: ICopilotUIMessage["parts"][number]): IAttendanceRunEnvelope | null {
  if (isToolUIPart(part)) {
    // A tool part only carries its output once it is finished; `preliminary` output is partial.
    if (part.state !== CopilotToolStateEnum.OUTPUT_AVAILABLE || part.preliminary) return null;

    return asAttendanceRunEnvelope(part.output);
  }

  // Narrowed rather than cast: only a `data-*` part carries a payload, and `in` is what the rest of
  // the Copilot helpers use to say so — a text or file part has no run to read.
  return "data" in part ? asAttendanceRunEnvelope(part.data) : null;
}

export function attendanceRunsOf(messages: ICopilotUIMessage[]): ICopilotStreamRead<IAttendanceRunEnvelope>[] {
  const runs: ICopilotStreamRead<IAttendanceRunEnvelope>[] = [];

  for (const message of messages) {
    if (
      message.parts.some(
        (part) =>
          part.type === `data-${CopilotDataPartEnum.ERROR}` &&
          normalizeCopilotError(part).code === CopilotErrorCodeEnum.MULTI_TIMESLOT_SESSION,
      )
    )
      continue;
    message.parts.forEach((part, index) => {
      const run = runOfPart(part);
      // A tool call keeps its id across stream ticks; a second call — a correction — gets a new one.
      if (run) runs.push({ key: isToolUIPart(part) ? part.toolCallId : `${message.id}#${index}`, payload: run });
    });
  }

  return runs;
}

/**
 * The answer carrying `activeRunId`, or `null` when no answer does — a sheet analysed over REST, or
 * a run restored from the route, is on screen without the chat ever having announced it.
 *
 * What it is for: the run's cards are rendered once, from live store state, so they have to be told
 * which answer owns them. Without it they follow whatever the assistant said last — and a reply
 * that produced no run at all ("Merci de préciser les critères …") inherited the picker the admin
 * had just disavowed, along with its ticked card and "Ouvrir les présences pré-remplies".
 *
 * Matched on the run id rather than on position: the conversation outlives the run it announced, so
 * "the latest attendance answer" and "the answer for the run on screen" come apart as soon as a
 * session-page upload or a route restore opens a *different* one. Attributed to that older answer,
 * the new cards paint under the wrong exchange — or vanish, once the admin has replied past it.
 *
 * A correction keeps the same run id on a new answer, so ownership still moves with it. A bare
 * proposal carrying no id of its own is matched by position, which is all it has.
 */
export function attendanceRunMessageIdOf(
  messages: ICopilotUIMessage[],
  activeRunId: string | null = null,
): string | null {
  let ownerMessageId: string | null = null;

  for (const message of messages) {
    for (const part of message.parts) {
      const run = runOfPart(part);
      if (!run || (activeRunId && run.runId !== activeRunId)) continue;

      ownerMessageId = message.id;
    }
  }

  return ownerMessageId;
}

/** Backwards-compatible proposal-only view used by the existing review derivation checks. */
export function asProposalEnvelope(payload: unknown): IAttendanceProposalEnvelope | null {
  const run = asAttendanceRunEnvelope(payload);
  if (!run?.proposal || run.status !== AttendanceRunStatusEnum.PROPOSAL) return null;

  return {
    runId: run.runId,
    sessionId: run.sessionId,
    timeslotId: run.timeslotId,
    document: run.document,
    proposal: run.proposal,
  };
}
